// src/pages/Folder/PerformanceDetail.tsx

import {
  AccountBalanceTwoTone,
  ArrowBackIosTwoTone,
  ArrowForwardIosTwoTone,
  CheckTwoTone,
  DownloadTwoTone,
  MoreVertTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import downloadjs from 'downloadjs'
import * as htmlToImage from 'html-to-image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Chart, {
  GoogleChartWrapperChartType,
  ReactGoogleChartEvent,
} from 'react-google-charts'
import { useNavigate, useLocation, useParams } from 'react-router-dom'

import { SensorId, SensorPool } from '@/types/domain'
import { FolderConsumption } from '@/types/domain/consumption'
import {
  FolderContractView,
  FolderId,
  FolderName,
  FolderSingle,
} from '@/types/domain/folder'

import {
  ASC,
  daysAgo,
  daysOnward,
  DDMMYYYY,
  DDMMYYYYHHMM,
  DMMMMYYYY,
  germanDecimals,
  getHoursArray,
  getMonthsArray,
  getYearsArray,
  hoursAgo,
  hoursOnward,
  initialChartConfig,
  isFullMonth,
  isMoreThenSevenDays,
  isMoreThenSixMonths,
  MMMYY,
  monthsAgo,
  monthsOnward,
  sensorType,
  sensorUnit,
  userLocales,
  WDDDMMYY,
  WDDDMMYYHHMM,
  SVGStatus,
  MMMMYYYY,
  WDDDMMYYYY,
  WDDDMMYYYYHHMM,
  YYYY,
  clampToLastFullMonth,
} from '../../api'
import {
  AM,
  AMItem,
  ChartTypeSelector,
  ConsumptionTable,
  DayFilterSelector,
  DlgConsumptionExport,
  DlgConsumptionTableCols,
  DlgMediumDiagnostics,
  GranularityDateSelector,
  GranularitySelector,
  HStack,
  KPage,
  KPISelector,
  MediumSelector,
  Preloader,
  SensorSelector,
  StackSelector,
  usePreloader,
} from '../../components'
import {
  FolderStatus,
  OperatorData,
  useAppConfig,
  useConsumptionApi,
  useFolder,
  useFolderArea,
  useFolderContract,
  useFolderName,
  useFolderSensorIds,
  useFolderStatus,
  useOperator,
  useSensorPool,
} from '../../hooks'
import useRenderCount from '../../hooks/tools/useRenderCount'

import { KPIViewer } from './components'

const DEFAULT_GRANULARITY = 'm' // m = daily
const DEFAULT_FROM = initialChartConfig.fromDate
const DEFAULT_TO = initialChartConfig.toDate

// 1000000 -> 1.000.000
const digitGrouping = digit =>
  Number(digit).toLocaleString(userLocales, germanDecimals)

const fmtCol = column => digitGrouping(column.toFixed(2))

const createComparisonTable = (
  actual: string,
  procentual: string,
  calculated: string
) => {
  if (procentual.includes('-') && procentual.length > 3)
    procentual = `▼ ${procentual}`

  if (!procentual.includes('-') && procentual.length > 3)
    procentual = `▲ ${procentual}`

  return { actual, procentual, calculated }
}

const getDeviation = (actual: number, calculated: number) =>
  ((actual - calculated) / calculated) * 100

const granularityTitle = granularity =>
  granularity === 'd'
    ? 'Stündlich'
    : granularity === 'm'
      ? 'Täglich'
      : granularity === 'y'
        ? 'Monatlich'
        : granularity === 'yy'
          ? 'Jährlich'
          : granularity

const checkAndReplaceNull = value => (value === null ? '-' : value)

const getValueOrDefault = (key, value) =>
  key === 'Gesamtverbrauch' ? '-' : checkAndReplaceNull(value)

const addValueIfValid = (value, sum) => (value !== '-' ? sum + value : sum)

const replaceZeroOrNaN = value => (isNaN(value) || value === 0 ? '-' : value)

const fmtDate = d => new Date(d).toLocaleDateString(userLocales, DMMMMYYYY)

type PerformanceQueryState = {
  g: string
  from: string
  to: string
  selected: number[]
  diagnosis?: boolean
  stacked?: boolean
  chartType?: GoogleChartWrapperChartType
  tableRows?: boolean
  showSettings?: boolean
}

const DEFAULT_QUERY: PerformanceQueryState = {
  g: DEFAULT_GRANULARITY,
  from: DEFAULT_FROM,
  to: DEFAULT_TO,
  selected: [],
  diagnosis: false,
  stacked: true,
  chartType: 'ColumnChart',
  tableRows: false,
  showSettings: false,
}

const usePerformanceQueryState = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const state = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const rawSelected = params.get('selected')
    const diagnosis = params.get('diagnosis') === '1'
    const stacked = params.get('stacked') === '1'
    const showSettings = params.get('showSettings') === '1'

    return {
      g: params.get('g') ?? DEFAULT_QUERY.g,
      from: params.get('from') ?? DEFAULT_QUERY.from,
      to: params.get('to') ?? DEFAULT_QUERY.to,
      selected: rawSelected
        ? rawSelected.split(',').map(Number).filter(Number.isFinite)
        : [],
      diagnosis,
      stacked,
      chartType: params.get('chartType') ?? DEFAULT_QUERY.chartType,
      tableRows: params.get('tableRows') === '1',
      showSettings,
    }
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    const normalized = new URLSearchParams()

    normalized.set('g', params.get('g') ?? DEFAULT_QUERY.g)
    normalized.set('from', params.get('from') ?? DEFAULT_QUERY.from)
    normalized.set('to', params.get('to') ?? DEFAULT_QUERY.to)

    const rawSelected = params.get('selected')
    if (rawSelected !== null) normalized.set('selected', rawSelected)

    if (params.get('stacked') === '1') normalized.set('stacked', '1')
    if (params.get('diagnosis') === '1') normalized.set('diagnosis', '1')
    if (params.get('tableRows') === '1') normalized.set('tableRows', '1')
    if (params.get('showSettings') === '1') normalized.set('showSettings', '1')

    const chartType = params.get('chartType')
    if (chartType && chartType !== DEFAULT_QUERY.chartType)
      normalized.set('chartType', chartType)

    const isEqual = params.toString() === normalized.toString()

    if (isEqual) return

    navigate(
      {
        pathname: location.pathname,
        search: `?${normalized.toString()}`,
      },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate])

  const update = useCallback(
    (patch: Partial<PerformanceQueryState>) => {
      const next = new URLSearchParams(location.search)

      if (patch.g !== undefined) next.set('g', patch.g)
      if (patch.from !== undefined) next.set('from', patch.from)
      if (patch.to !== undefined) next.set('to', patch.to)

      if (patch.selected !== undefined) {
        if (patch.selected.length === 0) next.delete('selected')
        else next.set('selected', patch.selected.join(','))
      }

      if (patch.diagnosis !== undefined) {
        if (patch.diagnosis) next.set('diagnosis', '1')
        else next.delete('diagnosis')
      }

      if (patch.stacked !== undefined) {
        if (patch.stacked) next.set('stacked', '1')
        else next.delete('stacked')
      }

      if (patch.chartType !== undefined) {
        if (patch.chartType === DEFAULT_QUERY.chartType)
          next.delete('chartType')
        else next.set('chartType', patch.chartType)
      }

      if (patch.tableRows !== undefined) {
        if (patch.tableRows) next.set('tableRows', '1')
        else next.delete('tableRows')
      }

      if (patch.showSettings !== undefined) {
        if (patch.showSettings) next.set('showSettings', '1')
        else next.delete('showSettings')
      }

      navigate(
        {
          pathname: location.pathname,
          search: `?${next.toString()}`,
        },
        { replace: true }
      )
    },
    [location.pathname, location.search, navigate]
  )

  return [state, update] as const
}

type SelectedDays = {
  workingdays: number
  weekends: number
  monday: number
  tuesday: number
  wednesday: number
  thursday: number
  friday: number
  saturday: number
  sunday: number
}

type PerformanceDetailProps = {
  title?: string
}

export const PerformanceDetail = ({
  title = 'Details Leistung',
}: PerformanceDetailProps) => {
  // render performance
  const rc = useRenderCount()
  console.log('PerformanceDetail', rc)

  const navigate = useNavigate()
  const { appConfig } = useAppConfig()
  const { isLoading, percentage } = usePreloader()

  const theme = useTheme()
  const { white: bgcolor } = theme.palette
  const black = theme.palette.black

  const opData: OperatorData = useOperator()

  const { id, type: initialMedium } = useParams<{ id: string; type: string }>()
  const fId: FolderId = Number(id)
  const fStatus: FolderStatus = useFolderStatus(fId)

  // performance detail states

  const defaultSelectedDays = {
    workingdays: 1,
    weekends: 1,
    monday: 1,
    tuesday: 1,
    wednesday: 1,
    thursday: 1,
    friday: 1,
    saturday: 1,
    sunday: 1,
  }

  const [actionMenu, setActionMenu] = useState<HTMLElement | null>(null)
  const [order, setOrder] = useState(ASC)
  const [medium, setMedium] = useState(initialMedium)
  const [exportModal, setExportModal] = useState<boolean>(false)
  const [fileName, setFileName] = useState<string>('')
  const [daysFilter, setDaysFilter] =
    useState<SelectedDays>(defaultSelectedDays)
  const [selectedDays, setSelectedDays] =
    useState<SelectedDays>(defaultSelectedDays)
  const [kpiData, setKpiData] = useState<any>({
    average: 0,
    max: 0,
    min: 0,
    total: 0,
    label: 'Verbrauch',
    unit: `${sensorUnit(initialMedium)}`,
    type: 'consumption',
  })
  const [periodData, setPeriodData] = useState<any>({
    days: 0,
    months: 0,
    showWarning: false,
    showDataWarning: false,
    warningText: '',
    warningDataText: '',
    title: '',
  })
  const [viewCols, setViewCols] = useState({
    date: true,
    actual_consumption: true,
    trend: true,
    counter_reading: true,
    emissions: true,
    area_consumption: true,
    employee_consumption: true,
    actual_costs: true,
    area_costs: true,
    employee_costs: true,
    benchmark: true,
  })

  const [unitConfig, setUnitConfig] = useState({
    header: false,
    values: true,
  })

  const sPool: SensorPool = useSensorPool()

  const [fromCompareDate, setFromCompareDate] = useState<string | undefined>()
  const [toCompareDate, setToCompareDate] = useState<string | undefined>()
  const [allSensors, setAllSensors] = useState<FolderConsumption[]>([])
  const [sensorColors, setSensorColors] = useState<Record<string, string>>({})
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [fillUpSensors, setFillUpSensors] = useState<any[]>([])

  const [chartData, setChartData] = useState([
    [' ', 'Verbrauch'],
    [new Date(), 0],
    [new Date(), 0],
  ])

  const [chartDataWithToolTip, setChartDataWithToolTip] = useState([
    [' ', 'Verbrauch'],
    [new Date(), 0],
    [new Date(), 0],
  ])
  const [sensorConsumptionData, setSensorConsumptionData] = useState<any[]>([])
  const [, setTableDataComparison] = useState<any[]>([])
  const [compareChartData, setCompareChartData] = useState<any[] | undefined>()
  const [tableDataExport, setTableDataExport] = useState<Record<string, any[]>>(
    {}
  )

  const [isStackDisabled, setStackDisabled] = useState<boolean>(true)
  const [isBrowseBackDisabled, setBrowseBackDisabled] = useState<boolean>(true)
  const [isBrowseForwardDisabled, setBrowseForwardDisabled] =
    useState<boolean>(true)
  const [isDaysFilterDisabled, setDaysFilterDisabled] = useState<boolean>(true)
  const [configChanged, setConfigChanged] = useState<boolean>(false)

  const {
    getFolderConsumption,
    getFolderCompareConsumption,
    getFolderMeterReading,
  } = useConsumptionApi()

  const [consumptionData, setConsumptionData] = useState<FolderConsumption[]>(
    []
  )

  const [compareConsumptionData, setCompareConsumptionData] = useState<
    FolderConsumption[]
  >([])

  const [meterReadingData, setMeterReadingData] = useState<any[]>([])

  const [consumptionReady, setConsumptionReady] = useState(false)

  const [isFactorError, setIsFactorError] = useState(false)
  const [factorError, setFactorError] = useState<any>(null)

  const [isFactorErrorMeterReading, setIsFactorErrorMeterReading] =
    useState(false)

  const [factorErrorSensorMeterReading, setFactorErrorSensorMeterReading] =
    useState<any>(null)

  const fData: FolderSingle = useFolder(fId)

  // helper functions (alphabetical order)

  const isDayInDays = useCallback(
    (day: number) => {
      if (selectedDays.monday === 1 && day === 1) return true
      if (selectedDays.tuesday === 1 && day === 2) return true
      if (selectedDays.wednesday === 1 && day === 3) return true
      if (selectedDays.thursday === 1 && day === 4) return true
      if (selectedDays.friday === 1 && day === 5) return true
      if (selectedDays.saturday === 1 && day === 6) return true
      if (selectedDays.sunday === 1 && day === 0) return true

      return false
    },
    [selectedDays]
  )

  const createConsumptionMap = useCallback(
    (data: any[], format: any) =>
      new Map(
        data.map(item => [
          new Date(item.timepoint).toLocaleString(userLocales, format),
          item,
        ])
      ),
    []
  )

  const formatDate = useCallback((date: any, format: any) => {
    if (typeof date === 'string') return date
    return new Date(date).toLocaleString(userLocales, format)
  }, [])

  const isBenchmarkOnDate = useCallback((benchmark: any, date: any) => {
    const targetDate = new Date(date)
    const periodStart = new Date(benchmark.attributes.period_start)
    const periodEnd = new Date(benchmark.attributes.period_end)
    return targetDate >= periodStart && targetDate <= periodEnd
  }, [])

  const opSIds: SensorId[] = useFolderSensorIds(fId)
  const fName: FolderName = useFolderName(fId)
  const contractData: FolderContractView = useFolderContract(fId)
  const areaInSqm = useFolderArea(fId) || 1

  // JSX handler (alphabetical order)

  const [query, setQuery] = usePerformanceQueryState()

  const granularityQuery = query.g
  const fromDateQuery = query.from
  const toDateQuery = query.to

  const [newParams, setNewParams] = useState({
    granularity: granularityQuery,
    fromDate: fromDateQuery,
    toDate: toDateQuery,
  })

  const browseBack = useCallback(() => {
    const npFromDate = fromDateQuery
    const npToDate = toDateQuery
    const { hours, days, months } = periodData

    if (granularityQuery === 'd') {
      const fromDate = hoursAgo(npFromDate, hours)
      const toDate = hoursAgo(npToDate, hours)

      setQuery({ from: fromDate, to: toDate })
      return
    }

    if (granularityQuery !== 'y') {
      const fromDate = daysAgo(npFromDate, days + 1)
      const toDate = daysAgo(npToDate, days + 1)

      setQuery({ from: fromDate, to: toDate })
      return
    }

    const fromDate = monthsAgo(npFromDate, months, true)
    const toDate = monthsAgo(npToDate, months, false)

    setQuery({ from: fromDate, to: toDate })
  }, [granularityQuery, periodData, fromDateQuery, toDateQuery, setQuery])

  const browseForward = useCallback(() => {
    const npFromDate = fromDateQuery
    const npToDate = toDateQuery
    const { hours, days, months } = periodData

    if (granularityQuery === 'd') {
      const fromDate = hoursOnward(npFromDate, hours)
      const toDate = hoursOnward(npToDate, hours)

      setQuery({ from: fromDate, to: toDate })
      return
    }

    if (granularityQuery !== 'y') {
      const fromDate = daysOnward(npFromDate, days + 1)
      const toDate = daysOnward(npToDate, days + 1)

      setQuery({ from: fromDate, to: toDate })
      return
    }

    const fromDate = monthsOnward(npFromDate, months, true)
    const toDate = monthsOnward(npToDate, months, false)

    setQuery({ from: fromDate, to: toDate })
  }, [granularityQuery, periodData, fromDateQuery, toDateQuery, setQuery])

  const applyConfigChange = () => {
    const { fromDate, toDate, granularity } = newParams

    setQuery({ from: fromDate, to: toDate, g: granularity })

    if (JSON.stringify(selectedDays) !== JSON.stringify(daysFilter))
      setSelectedDays(daysFilter)
  }

  const closeAM = () => setActionMenu(null)

  const closeDiagnostics = () => setQuery({ diagnosis: false })

  const closeExportDialog = () => setExportModal(false)

  const closeTableRowsDialog = () => setQuery({ tableRows: false })

  const configureTableColumns = (cols, unitCfg) => {
    setViewCols(cols)
    setUnitConfig(unitCfg)
  }

  const exportAsJPEG = () => handleExportImage('jpg')

  const exportAsPNG = () => handleExportImage('png')

  const handleChartType = (_: any, chartType: any) => {
    if (chartType !== null) setQuery({ chartType })
  }

  const handleDaysFilter = (days: SelectedDays) => setDaysFilter(days)

  const handleExportImage = async (type = 'jpeg') => {
    const node = document.querySelector('.image-export') as HTMLElement | null
    if (!node) return
    if (type === 'png') {
      htmlToImage
        .toPng(node)
        .then(dataUrl => {
          const img = new Image()
          img.src = dataUrl
          downloadjs(dataUrl, `${fileName}.${type}`, `image/${type}`)

          return
        })
        .catch(error => {
          console.error('Feler beim Exportieren!', error)
        })
    } else {
      htmlToImage
        .toJpeg(node, { quality: 0.95 })
        .then(dataUrl => {
          const img = new Image()
          img.src = dataUrl
          downloadjs(dataUrl, `${fileName}.${type}`, `image/${type}`)

          return
        })
        .catch(error => {
          console.error('Feler beim Exportieren!', error)
        })
    }
  }

  const handleFromDate = (fromDate: any) => {
    setNewParams(prev => {
      let toDate = prev.toDate

      if (toDate && fromDate > toDate) toDate = fromDate

      return {
        ...prev,
        fromDate,
        toDate,
      }
    })
  }

  const handleGranularity = (event: any) => {
    const granularity = event.target.value

    setNewParams(prev => {
      let fromDate = prev.fromDate
      let toDate = prev.toDate

      if (granularity === 'd') {
        fromDate = initialChartConfig.fromDateDay
        toDate = initialChartConfig.toDateDay
      }

      if (granularity === 'm') {
        fromDate = initialChartConfig.fromDate
        toDate = initialChartConfig.toDate
      }

      if (granularity === 'y') {
        if (fromDate) {
          const d = new Date(fromDate)
          const y = d.getFullYear()
          const m = d.getMonth()

          fromDate = `${y}-${String(m + 1).padStart(2, '0')}-01`
        }
        if (toDate) {
          const d = new Date(toDate)
          const y = d.getFullYear()
          const m = d.getMonth()
          const last = new Date(y, m + 1, 0).getDate()

          toDate = `${y}-${String(m + 1).padStart(2, '0')}-${last}`
          toDate = clampToLastFullMonth(toDate)
        }

        if (fromDate && toDate && toDate < fromDate) {
          const d = new Date(toDate)
          const y = d.getFullYear()
          const m = d.getMonth()
          fromDate = `${y}-${String(m + 1).padStart(2, '0')}-01`
        }
      }

      if (granularity === 'yy') {
        if (fromDate) {
          const y = fromDate.slice(0, 4)
          fromDate = `${y}-01-01`
        }

        if (toDate) {
          const y = toDate.slice(0, 4)
          toDate = `${y}-12-31`
        }
      }

      return {
        ...prev,
        granularity,
        fromDate,
        toDate,
      }
    })
  }

  const handleKpiType = (event: any) => {
    const type = event.target.value
    const unit =
      type === 'consumption'
        ? sensorUnit(medium)
        : type === 'price'
          ? '€'
          : type === 'co2'
            ? 'kg'
            : sensorUnit(medium)

    setKpiData(prev => ({ ...prev, type, unit }))
  }

  const handleMedium = (event: any) => {
    if (kpiData.type === 'consumption') {
      const unit = sensorUnit(event.target.value)
      setKpiData(prev => ({ ...prev, unit }))
    }
    setMedium(event.target.value)
    navigate(`/folder/${fId}/detail/${event.target.value}${location.search}`)
  }

  const handleOrder = (order: any) => setOrder(order)

  const handleSelectedSensors = (result: number[]) =>
    updateSelectedQuery(result)

  const handleSettings = () => {
    if (!opData?.isAdmin) return

    setQuery({ showSettings: !query.showSettings })
  }

  const handleStackMode = (event: any, stacked: boolean | null) => {
    if (stacked === null) return

    setQuery({ stacked })
  }

  const handleToDate = (toDate: any) => {
    setNewParams(prev => {
      let fromDate = prev.fromDate

      if (fromDate && toDate < fromDate) fromDate = toDate

      return {
        ...prev,
        toDate,
        fromDate,
      }
    })
  }

  const openAM = (event: any) => setActionMenu(event.currentTarget)

  const openDiagnostics = () => setQuery({ diagnosis: true })

  const openExportDialog = () => setExportModal(true)

  const openTableRowsDialog = () => setQuery({ tableRows: true })

  const gotoFolderDetails = () => navigate(`/folder/${fId}`)
  const gotoPerformanceOverview = () =>
    navigate(`/folder/${fId}/home/performance`)

  const gotoInstallationOverview = () =>
    navigate(`/folder/${fId}/home/installation`)

  const gotoCorrelation = () => navigate(`/folder/${fId}/home/correlation`)

  const gotoSecurity = () => navigate(`/folder/${fId}/home/security`)
  const gotoFeeling = () => navigate(`/folder/${fId}/home/feeling`)

  // memoized

  const selectedSensors = query.selected

  const didHydrateSelectedRef = useRef(false)

  const updateSelectedQuery = useCallback(
    values => {
      const nextValues = values.map(Number).filter(Number.isFinite)
      const currentValues = query.selected.map(Number).filter(Number.isFinite)

      if (!didHydrateSelectedRef.current) {
        if (allSensors.length === 0) return

        didHydrateSelectedRef.current = true
      }

      if (nextValues.join(',') === currentValues.join(',')) return

      setQuery({ selected: nextValues })
    },
    [allSensors.length, query.selected, setQuery]
  )

  useEffect(() => {
    setNewParams(prev => ({
      ...prev,
      granularity: granularityQuery,
      fromDate: fromDateQuery,
      toDate: toDateQuery,
    }))
  }, [granularityQuery, fromDateQuery, toDateQuery])

  const consumptionMaps = useMemo(() => {
    const map = new Map()

    const format =
      granularityQuery === 'd'
        ? WDDDMMYYYYHHMM
        : granularityQuery === 'm'
          ? WDDDMMYYYY
          : granularityQuery === 'y'
            ? MMMMYYYY
            : granularityQuery === 'yy'
              ? YYYY
              : DDMMYYYY

    sensorConsumptionData.forEach(sensor => {
      const values = sensor?.consumption?.values || []
      map.set(sensor?.name, createConsumptionMap(values, format))
    })

    return map
  }, [sensorConsumptionData, granularityQuery, createConsumptionMap])

  const sensorByNameMap = useMemo(() => {
    const map = new Map()
    sensorConsumptionData.forEach(s => map.set(s.name, s))
    return map
  }, [sensorConsumptionData])

  const emptyChartData = useMemo(
    () => [
      [granularityTitle(granularityQuery), 'Verbrauch'],
      [new Date(fromDateQuery), 0],
      [new Date(toDateQuery), 0],
    ],
    [granularityQuery, fromDateQuery, toDateQuery]
  )

  const selectedSensorsSet = useMemo(
    () => new Set(selectedSensors),
    [selectedSensors]
  )

  const colors = useMemo(() => {
    const list = allSensors
      .filter(s => selectedSensorsSet.has(s.entity_id))
      .map(s => sensorColors[String(s.name)])

    return list.length > 0 ? list : [black]
  }, [allSensors, selectedSensorsSet, sensorColors, black])

  const chartTicks = useMemo(
    () => chartDataWithToolTip.slice(1).map(row => row[0] as Date),
    [chartDataWithToolTip]
  )

  const chartOptions = useMemo(
    () => ({
      backgroundColor: 'white',
      colors: selectedSensors.length === 0 ? undefined : colors,
      tooltip: { isHtml: true, trigger: 'visible' },
      isStacked: query.stacked,
      legend: { position: 'none' },
      vAxis: {
        format: `#,###.## ${kpiData.unit}`,
        viewWindow: {
          min: 0,
          max: selectedSensors.length === 0 ? 100 : kpiData.max,
        },
      },
      hAxis: {
        format:
          granularityQuery === 'd'
            ? 'EEE, dd.MM. HH:mm'
            : granularityQuery === 'm'
              ? 'EEE, dd.MM.'
              : granularityQuery === 'y'
                ? 'MMM yy'
                : 'yyyy',
        viewWindow: {
          min:
            selectedSensors.length === 0 ? new Date(fromDateQuery) : undefined,
          max: selectedSensors.length === 0 ? new Date(toDateQuery) : undefined,
        },
        textStyle: { fontSize: 10 },
        ticks: chartTicks,
        gridlines: { count: 12 },
      },
    }),
    [
      selectedSensors.length,
      colors,
      query.stacked,
      granularityQuery,
      fromDateQuery,
      toDateQuery,
      kpiData.unit,
      kpiData.max,
      chartTicks,
    ]
  )

  const chartEvents = useMemo<ReactGoogleChartEvent[]>(
    () => [
      {
        eventName: 'ready',
        callback: (props: { chartWrapper: any; google: any }) => {
          const chart = props.chartWrapper.getChart()

          props.google.visualization.events.addListener(
            chart,
            'error',
            (errorMessage: any) => {
              props.google.visualization.errors.removeError(errorMessage.id)
            }
          )
        },
      },
    ],
    []
  )

  const tableKey = useMemo(
    () => selectedSensors.slice().sort().join(',') || 'none',
    [selectedSensors]
  )

  const sensorsUnderChart = useMemo(
    () =>
      (!showInfo && selectedSensors.length > 10 && exportModal) ||
      (showInfo && selectedSensors.length > 5 && exportModal),
    [showInfo, selectedSensors.length, exportModal]
  )

  const consumptionMapsByName = useMemo(() => {
    const map = new Map()

    const format =
      granularityQuery === 'd'
        ? WDDDMMYYHHMM
        : granularityQuery === 'm'
          ? WDDDMMYY
          : granularityQuery === 'y'
            ? MMMYY
            : WDDDMMYY

    sensorConsumptionData.forEach(sensor => {
      const values = sensor?.consumption?.values || []
      map.set(sensor.name, createConsumptionMap(values, format))
    })

    return map
  }, [sensorConsumptionData, granularityQuery, createConsumptionMap])

  const sensorByIdMap = useMemo(() => {
    const map = new Map()
    sensorConsumptionData.forEach(s => map.set(s.entity_id, s))
    return map
  }, [sensorConsumptionData])

  const opSIdsSet = useMemo(() => new Set(opSIds.map(Number)), [opSIds])

  const operatorConsumptionByName = useMemo(() => {
    const map = new Map()

    consumptionData
      .filter(f => opSIdsSet.has(Number(f.entity_id)))
      .forEach(f => map.set(f.name, f))

    return map
  }, [consumptionData, opSIdsSet])

  const meterReadingByName = useMemo(() => {
    const map = new Map()
    meterReadingData.forEach(item => map.set(item.name, item))
    return map
  }, [meterReadingData])

  const MemoChart = useMemo(
    () => (
      <Chart
        chartLanguage={'de_DE'}
        chartType={query.chartType as GoogleChartWrapperChartType}
        chartEvents={chartEvents}
        data={
          selectedSensors.length === 0 ? emptyChartData : chartDataWithToolTip
        }
        height={500}
        options={chartOptions}
      />
    ),
    [
      query.chartType,
      chartEvents,
      chartDataWithToolTip,
      emptyChartData,
      chartOptions,
      selectedSensors.length,
    ]
  )

  // useEffect (dependency count order)

  useEffect(() => {
    if (!opData?.view_options) return

    const { view_options } = opData
    const voCurrent = view_options.find(v => v.folder_id === fId)
    const voGlobal = view_options.find(v => v.folder_id === 0)
    // leere columns = Sentinel "alle Gebäude gilt" → Global-Record verwenden
    const columnSource = voCurrent?.columns ? voCurrent : voGlobal

    if (!columnSource?.columns) return

    const columns = columnSource.columns
      .split('x')[1]
      ?.split('')
      ?.map(item => Number(item))
      ?.join(',')

    const updatedVC = {
      date: true,
      actual_consumption: true,
      trend: true,
      counter_reading: true,
      emissions: true,
      area_consumption: true,
      employee_consumption: true,
      actual_costs: true,
      area_costs: true,
      employee_costs: true,
      benchmark: true,
    }

    if (columns) {
      const keys = Object.keys(updatedVC)

      for (let col = 0; col < keys.length; col++)
        updatedVC[keys[col]] = Boolean(Number(columns.split(',')[col]))

      setViewCols(updatedVC)
    }

    const ucCfg = columnSource.columns
      .split('x')[0]
      .split('')
      .map(f => Boolean(Number(f)))

    if (ucCfg.length >= 2) setUnitConfig({ header: ucCfg[0], values: ucCfg[1] })
  }, [opData, fId])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const from = fromDateQuery?.slice(0, 10)
        const to = toDateQuery?.slice(0, 10)
        const data = await getFolderConsumption(fId, granularityQuery, from, to)

        setConsumptionData(data)
      } catch (error) {
        setIsFactorError(true)
        setFactorError(error.sensor)
        console.error('Error fetching consumption data:', error)
      } finally {
        setConsumptionReady(true)
      }
    }

    fetchData()
  }, [getFolderConsumption, fId, fromDateQuery, toDateQuery, granularityQuery])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const from = fromCompareDate?.slice(0, 10)
        const to = toCompareDate?.slice(0, 10)
        const data = await getFolderCompareConsumption(
          fId,
          granularityQuery,
          from,
          to
        )

        setCompareConsumptionData(data)
      } catch (error) {
        setIsFactorError(true)
        setFactorError(error.sensor)
        console.error('Error fetching compare consumption data:', error)
      }
    }

    fetchData()
  }, [
    getFolderCompareConsumption,
    fId,
    granularityQuery,
    fromCompareDate,
    toCompareDate,
  ])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const meterReadingGranularity =
          granularityQuery === 'm'
            ? 'd'
            : granularityQuery === 'y'
              ? 'm'
              : granularityQuery

        const data = await getFolderMeterReading(
          fId,
          fromDateQuery?.slice(0, 10),
          toDateQuery?.slice(0, 10),
          meterReadingGranularity
        )

        setMeterReadingData(data)
      } catch (error) {
        setIsFactorErrorMeterReading(true)
        setFactorErrorSensorMeterReading(error.sensor)
      }
    }

    fetchData()
  }, [getFolderMeterReading, fId, fromDateQuery, toDateQuery, granularityQuery])

  const prevMedium = useRef(medium)

  useEffect(() => {
    if (prevMedium.current === medium) return
    prevMedium.current = medium

    setFillUpSensors([])
    setQuery({ selected: [] })
  }, [medium, setQuery])

  useEffect(() => {
    setStackDisabled(selectedSensors.length < 2)
  }, [selectedSensors])

  useEffect(() => {
    setDaysFilterDisabled(newParams?.granularity !== 'm')
  }, [newParams?.granularity])

  useEffect(() => {
    if (!fName || !medium) return

    setFileName(`${fName}_${sensorType(medium)}`)
  }, [fName, medium])

  useEffect(() => {
    if (!contractData) return

    const showWarning = periodData?.showWarning

    const beforeContract = fromDateQuery < contractData?.date_begin
    const afterContract = toDateQuery > contractData?.date_expire

    const TO = new Date(toDateQuery)
    TO.setHours(23, 59, 59)

    const FROM = new Date(fromDateQuery)
    FROM.setHours(0, 0, 0)

    const range = TO.getTime() - FROM.getTime()

    const dateInFuture =
      new Date(fromDateQuery).getTime() + range > Date.now() ||
      new Date(toDateQuery).getTime() + range > Date.now()

    setBrowseBackDisabled(showWarning || beforeContract)
    setBrowseForwardDisabled(showWarning || afterContract || dateInFuture)
  }, [fromDateQuery, toDateQuery, contractData, periodData?.showWarning])

  useEffect(() => {
    if (!fromDateQuery || !toDateQuery) return

    const hours = () => {
      const _1H = 60 * 60 * 1000

      const from = new Date(fromDateQuery).getTime()
      const to = new Date(toDateQuery).getTime()

      return (to - from) / _1H
    }

    const days = () => {
      const _1H = 60 * 60 * 1000
      const _24H = 24 * _1H
      const from: number = new Date(fromDateQuery).getTime()
      const to: number = new Date(toDateQuery).getTime()

      return (to - from) / _24H
    }

    const months = () => {
      const from: Date = new Date(toDateQuery)
      const to: Date = new Date(fromDateQuery)

      let range = (to.getFullYear() - from.getFullYear()) * 12
      range -= from.getMonth()
      range += to.getMonth()

      return range > 0 ? range : 1
    }

    const Y_GRANULARITY: boolean = newParams?.granularity === 'y'
    const M_GRANULARITY: boolean = newParams?.granularity === 'm'
    const D_GRANULARITY: boolean = newParams?.granularity === 'd'

    const IS_HALF_MONTH: boolean =
      isFullMonth(newParams?.fromDate, newParams?.toDate) === false

    const EXCEEDS_HALFYEAR: boolean =
      isMoreThenSixMonths(newParams?.fromDate, newParams?.toDate) === true

    const EXCEEDS_WEEK: boolean =
      isMoreThenSevenDays(newParams?.fromDate, newParams?.toDate) === true

    const checkForSensorWithoutData = (data: any[], from: any) => {
      if (!data || data.length === 0) return false

      const fromDate: number = new Date(from).getTime()

      const hasData = data.some(record => {
        const { data_sampling_date: dsd, entity_id } = record
        const timestamp: number = new Date(dsd).getTime()

        return timestamp > fromDate && selectedSensorsSet.has(entity_id)
      })

      return hasData
    }

    const sensorWithoutData = checkForSensorWithoutData(
      consumptionData,
      fromDateQuery
    )
    const showDataWarning: boolean =
      sensorWithoutData && appConfig?.showMissingSensorDataNotice ? true : false

    const showWarning: boolean =
      Y_GRANULARITY && IS_HALF_MONTH
        ? true
        : M_GRANULARITY && EXCEEDS_HALFYEAR
          ? true
          : D_GRANULARITY && EXCEEDS_WEEK
            ? true
            : false

    const title = `${fmtDate(fromDateQuery)} - ${fmtDate(toDateQuery)}`

    const warningDataText =
      'Für bestimmte Messpunkte stehen im gewählten Zeitraum keine vollständigen Daten zur Verfügung. Mögliche Gründe: Die Messung begann erst zu einem späteren Zeitpunkt oder wurde unterbrochen.'

    const warningText =
      Y_GRANULARITY && IS_HALF_MONTH
        ? 'Diese Ansicht ist nur bei Auswahl von vollen Monaten (Erster bis letzter Tag des Monats) verfügbar.'
        : M_GRANULARITY && EXCEEDS_HALFYEAR
          ? 'Diese Ansicht erlaubt eine Zeitspanne von maximal 6 Monaten.'
          : D_GRANULARITY && EXCEEDS_WEEK
            ? 'Diese Ansicht erlaubt eine Zeitspanne von maximal 7 Tagen.'
            : ''

    // set compare fromDate and toDate relative to value of periodData.days
    setFromCompareDate(daysAgo(fromDateQuery, days() + 1))
    setToCompareDate(daysAgo(toDateQuery, days() + 1))

    setPeriodData(prev => ({
      ...prev,
      hours: hours(),
      days: days(),
      months: months(),
      showWarning,
      showDataWarning,
      title,
      warningText,
      warningDataText,
    }))
  }, [
    appConfig,
    fromDateQuery,
    toDateQuery,
    granularityQuery,
    newParams?.granularity,
    newParams?.fromDate,
    newParams?.toDate,
    consumptionData,
    selectedSensorsSet,
  ])

  useEffect(() => {
    if (chartData === undefined) return

    const addTooltipColumn = (data: any[]) => {
      const combined = []

      const dateFormat =
        granularityQuery === 'd'
          ? WDDDMMYYYYHHMM
          : granularityQuery === 'm'
            ? WDDDMMYYYY
            : granularityQuery === 'y'
              ? MMMMYYYY
              : granularityQuery === 'yy'
                ? YYYY
                : DDMMYYYY

      data.forEach((row, i) => {
        if (i === 0) {
          combined.push([row[i]])

          row.forEach((col, j) => {
            if (j > 0) {
              combined[i].push(col, {
                type: 'string',
                role: 'tooltip',
                p: { html: true },
              })
            }
          })
        } else {
          combined[i] = [row[0]]
          row.forEach((col, j) => {
            if (j > 0) {
              const name = data[0][j]
              const unit = kpiData.unit
              const timestamp =
                combined[i][0] instanceof Date &&
                !isNaN(combined[i][0].getTime())
                  ? combined[i][0].toLocaleString(userLocales, dateFormat)
                  : combined[i][0]

              const sensor = sensorByNameMap.get(name)
              if (sensor) {
                const consumptionMap = consumptionMaps.get(name)

                const timepoint = formatDate(combined[i][0], dateFormat)

                const data = consumptionMap.get(timepoint)

                if (kpiData.type === 'co2') col = data?.co2 ?? 0
                if (kpiData.type === 'price') col = data?.cost ?? 0
              }

              combined[i][0] = timestamp
              combined[i].push(
                col,
                `<div style="padding:5px 10px; min-width:220px;"><p><strong>${timestamp}</strong></p><p>${name}: <strong>${digitGrouping(
                  col
                )} ${unit}</strong></p></div>`
              )
            }
          })
        }
      })

      return combined
    }

    if (chartData.length > 1)
      setChartDataWithToolTip(addTooltipColumn(chartData))
  }, [
    chartData,
    granularityQuery,
    kpiData.type,
    kpiData.unit,
    consumptionMaps,
    sensorByNameMap,
    formatDate,
  ])

  useEffect(() => {
    if (compareConsumptionData.length === 0 || selectedSensors.length === 0) {
      setCompareChartData(undefined)
      setTableDataExport({})
      return
    }

    const IS_OPERATOR_SENSOR = f => opSIdsSet.has(Number(f.entity_id))

    const IS_CONSUMPTION_SENSOR = f =>
      medium === 'energy'
        ? f.sensor_type === 'energy' || f.sensor_type === 'energy_detail'
        : medium === 'water'
          ? f.sensor_type === 'water' || f.sensor_type === 'water_detail'
          : medium === 'gas'
            ? f.sensor_type === 'gas' || f.sensor_type === 'gas_detail'
            : f.sensor_type === medium

    const _allSensors = compareConsumptionData
      .filter(IS_OPERATOR_SENSOR)
      .filter(IS_CONSUMPTION_SENSOR)
      .sort((a, b) => {
        const result = String(a.name).localeCompare(String(b.name))

        return order === ASC ? result : -result
      })

    const format =
      granularityQuery === 'd'
        ? DDMMYYYYHHMM
        : granularityQuery === 'm'
          ? DDMMYYYY
          : granularityQuery === 'y'
            ? MMMYY
            : granularityQuery === 'yy'
              ? YYYY
              : DDMMYYYY

    let columns = [granularityTitle(granularityQuery)]
    let _data = []
    const _fillUps = []

    _allSensors
      .filter(f => selectedSensorsSet.has(f.entity_id))
      .forEach(s => {
        const { consumption, id, name } = s

        columns = [...columns, name]

        consumption.values.forEach((item, key) => {
          const { timepoint, value: recValue } = item
          const value = Number(recValue.toFixed(2))

          _data[key] = _data[key] ? [..._data[key], value] : [timepoint, value]

          // missing sensor data needs to be filled up with 0 to render chart
          if (columns.length > _data[key].length) {
            const fillUps = new Array(columns.length - _data[key].length).fill(
              0
            )

            _data[key] = [..._data[key], ...fillUps]

            if (_fillUps.length === 0)
              _fillUps.push({ id, amount: fillUps.length })
          }
        })
      })

    setFillUpSensors(_fillUps)

    _data.sort((a, b) => (a[0] > b[0] ? 1 : -1))
    _data = _data.map(item => {
      item[0] = new Date(item[0]).toLocaleString(userLocales, format)

      return item
    })

    // add columns header (sensor names) on top of data
    _data = [columns, ..._data]

    // add dummy data if no data to render
    if (_data.length === 1) _data.push(columns.map(() => 0))

    // validate data integrity and add fillup data if needed
    for (const i in _data) {
      const fillUps = new Array(columns.length - _data[i].length).fill(0)

      _data[i] = [..._data[i], ...fillUps]
    }

    setCompareChartData(_data)
  }, [
    compareConsumptionData,
    granularityQuery,
    medium,
    opSIdsSet,
    order,
    selectedSensors.length,
    selectedSensorsSet,
  ])

  useEffect(() => {
    setConfigChanged(
      periodData.showWarning ||
        (newParams?.granularity === granularityQuery &&
          newParams?.fromDate === fromDateQuery &&
          newParams?.toDate === toDateQuery &&
          JSON.stringify(selectedDays) === JSON.stringify(daysFilter))
    )
  }, [
    granularityQuery,
    fromDateQuery,
    toDateQuery,
    daysFilter,
    newParams?.granularity,
    newParams?.fromDate,
    newParams?.toDate,
    periodData.showWarning,
    selectedDays,
  ])

  useEffect(() => {
    if (consumptionData.length === 0) {
      setChartData(emptyChartData)
      return
    }

    const getDaysArray = (dateFrom, dateTo) => {
      const days = []
      const date = new Date(dateFrom)

      for (
        days;
        date <= dateTo.setHours(23, 59, 59);
        date.setDate(date.getDate() + 1)
      )
        if (isDayInDays(date.getDay())) days.push([new Date(date)])

      if (days.length === 0) days.push([new Date(date)])

      return days
    }

    const IS_OPERATOR_SENSOR = f => opSIdsSet.has(Number(f.entity_id))

    const IS_CONSUMPTION_SENSOR = f =>
      medium === 'energy'
        ? f.type_in_folder === 'energy' || f.type_in_folder === 'energy_detail'
        : medium === 'water'
          ? f.type_in_folder === 'water' || f.type_in_folder === 'water_detail'
          : medium === 'gas'
            ? f.type_in_folder === 'gas' || f.type_in_folder === 'gas_detail'
            : f.type_in_folder === medium

    const IS_ENERGY_TYPE_SENSOR = f =>
      f.type_in_folder === medium || f.type_in_folder === `${medium}_detail`

    const _allSensors = consumptionData
      .filter(IS_OPERATOR_SENSOR)
      .filter(IS_CONSUMPTION_SENSOR)
      .sort((a, b) => {
        const result = String(a.name).localeCompare(String(b.name))

        return order === ASC ? result : -result
      })

    const _sensorColors: Record<string, string> = {}

    const fittingSensors = consumptionData
      .filter(IS_OPERATOR_SENSOR)
      .filter(IS_ENERGY_TYPE_SENSOR)

    if (!fittingSensors[0])
      console.warn('no operator sensors for medium in consumption data')
    const allSensorConsumptionData = []

    const createBaseData = (granularity, fromDate, toDate) => {
      let columns = [granularityTitle(granularity)]

      const format =
        granularity === 'd'
          ? WDDDMMYYHHMM
          : granularity === 'm'
            ? WDDDMMYY
            : granularity === 'y'
              ? MMMYY
              : granularity === 'yy'
                ? YYYY
                : WDDDMMYY

      let data =
        granularity === 'd'
          ? getHoursArray(new Date(fromDate), new Date(toDate))
          : granularity === 'm'
            ? getDaysArray(new Date(fromDate), new Date(toDate))
            : granularity === 'y'
              ? getMonthsArray(fromDate, toDate)
              : granularity === 'yy'
                ? getYearsArray(fromDate, toDate)
                : []

      if (granularity === 'd') data = data.map(day => [day])
      if (granularity === 'y') data = data.map(day => [day])

      _allSensors
        .filter(f => selectedSensorsSet.has(f.entity_id))
        .forEach(s => {
          const { consumption, name, chartColor } = s
          allSensorConsumptionData.push(s)

          const consumptionMap = createConsumptionMap(
            consumption.values,
            format
          )

          const addConsumption = record => {
            const timepoint = formatDate(record[0], format)
            const data = consumptionMap.get(timepoint)

            const aggregated =
              data && typeof data.value === 'number' ? data.value : 0

            return [
              ...record,
              aggregated === null ? aggregated : +aggregated.toFixed(2),
            ]
          }

          columns = [...columns, name]

          data = data.map(record => addConsumption(record))
          _sensorColors[String(name)] = chartColor
        })

      setAllSensors(_allSensors)
      setSensorColors(_sensorColors)

      data.sort((a, b) =>
        new Date(a[0]).getTime() > new Date(b[0]).getTime() ? 1 : -1
      )

      // fill columns data to minimum if no data
      if (data.length === 0) data = [granularity, granularity]

      // add columns header (sensor names) on top of data
      data = [columns, ...data]

      // add dummy data if no data to render
      if (data.length === 1) data.push(columns.map(() => 0))

      return data
    }

    setChartData(createBaseData(granularityQuery, fromDateQuery, toDateQuery))

    setSensorConsumptionData(allSensorConsumptionData)
  }, [
    fromDateQuery,
    toDateQuery,
    granularityQuery,
    consumptionData,
    isDayInDays,
    medium,
    opSIdsSet,
    order,
    selectedSensorsSet,
    createConsumptionMap,
    formatDate,
    emptyChartData,
    selectedSensors.length,
  ])

  useEffect(() => {
    if (selectedSensors.length === 0) {
      setKpiData(prev => ({ ...prev, average: 0, max: 0, min: 0, total: 0 }))
      return
    }

    let average = 0
    let max = 0
    let min = 10000000
    let total = 0

    const createKpiData = () => {
      const maxSumObject: Record<string, number> = {}

      for (const entity_id of selectedSensors) {
        const sensor = sensorByIdMap.get(entity_id)
        if (!sensor?.consumption?.values?.length) continue

        if (sensor) {
          const { consumption, name } = sensor

          for (let i = 0; i < consumption.values.length; i++) {
            const { timepoint, value, co2, cost } = consumption.values[i]

            if (
              !isDayInDays(new Date(timepoint).getDay()) &&
              granularityQuery !== 'y'
            )
              continue

            const time = new Date(timepoint)
            const from = new Date(fromDateQuery)
            const to = new Date(toDateQuery)

            if (granularityQuery === 'd' && (time < from || time > to)) continue

            const amount =
              kpiData.type === 'consumption'
                ? value
                : kpiData.type === 'price'
                  ? cost
                  : kpiData.type === 'co2'
                    ? co2
                    : value
            total += amount

            if (query.stacked) {
              maxSumObject[timepoint] = maxSumObject[timepoint]
                ? maxSumObject[timepoint] + amount
                : amount
            } else {
              maxSumObject[String(`${name} + ${i}`)] = amount
            }
          }
        }
      }

      Object.values(maxSumObject).forEach(peak => {
        if (peak < min) min = peak
        if (peak > max) max = peak
      })

      if (total > 0) {
        const sum = Object.values(maxSumObject).reduce(
          (acc, val) => acc + val,
          0
        )
        average = sum / Object.values(maxSumObject).length
      }

      if (min === 10000000) min = 0

      if (isNaN(total) || total === undefined) total = 0

      if (medium === 'water' && kpiData.type === 'co2')
        total = max = min = average = 0
    }

    createKpiData()
    setKpiData(prev => ({ ...prev, average, max, min, total }))
  }, [
    fromDateQuery,
    toDateQuery,
    granularityQuery,
    query.stacked,
    isDayInDays,
    kpiData.type,
    medium,
    selectedSensors,
    sensorByIdMap,
  ])

  useEffect(() => {
    if (
      chartData === undefined ||
      fData === null ||
      compareChartData === undefined ||
      compareChartData === null
    )
      return

    const result: Record<string, any[]> = {}
    result['Gesamtverbrauch'] = []

    const format =
      granularityQuery === 'd'
        ? WDDDMMYYHHMM
        : granularityQuery === 'm'
          ? WDDDMMYY
          : granularityQuery === 'y'
            ? MMMYY
            : WDDDMMYY

    const getTotalConsumption = data => {
      let dates = []
      const consumption = []

      data[0].forEach((sheet, key) => {
        const title = data[0][key + 1]
        if (!title) return

        result[title] = []

        for (const item of data) {
          if (!item || !item[0]) return

          const date = item[0]
          const value = item[key + 1]

          if (value !== undefined && typeof value !== 'string') {
            result[title].push([date, value])
            consumption.push([date, value])
            dates.push(date)
          }
        }
      })

      dates = [...new Set(dates)]
      dates.forEach((date, key) => {
        dates[key] = [date, 0]

        consumption.forEach(value => {
          if (value[0] === dates[key][0]) dates[key][1] += value[1]
        })
      })

      dates.sort((a, b) =>
        new Date(a[0]).getTime() > new Date(b[0]).getTime() ? 1 : -1
      )

      return dates
    }

    result['Gesamtverbrauch'] = getTotalConsumption(chartData)

    let comparisonRows: any[] = []
    let monthSum = 0

    const {
      target_value_annual_electricity_consumption,
      target_value_annual_gas_consumption,
    } = fData

    const comparisonValue =
      (medium === 'energy' || medium === 'energy_detail') &&
      target_value_annual_electricity_consumption !== null
        ? target_value_annual_electricity_consumption
        : medium === 'gas' && target_value_annual_gas_consumption !== null
          ? target_value_annual_gas_consumption
          : medium === 'water'
            ? 0
            : 1

    if (compareChartData.slice(1).length < 12) {
      compareChartData.slice(1).forEach(entries =>
        entries.slice(1).forEach(record => {
          monthSum += record
        })
      )

      const calculatedSum =
        ((monthSum / compareChartData.slice(1).length) * 12) / areaInSqm

      const deviation = getDeviation(calculatedSum, comparisonValue)

      comparisonRows.push(
        createComparisonTable(
          `${fmtCol(calculatedSum)} ${sensorUnit(medium)}`,
          `${fmtCol(deviation)} %`,
          `${fmtCol(comparisonValue)} ${sensorUnit(medium)}`
        )
      )
    }

    if (compareChartData.slice(1).length < 6) {
      comparisonRows = []
      comparisonRows.push(
        createComparisonTable(
          ` - `,
          ` - `,
          `${comparisonValue.toFixed(2)} ${sensorUnit(medium)}`
        )
      )
    }

    if (compareChartData.slice(1).length >= 12) {
      compareChartData.slice(1).forEach(entries =>
        entries.slice(1).forEach(record => {
          monthSum += record
        })
      )

      monthSum /= areaInSqm
      const deviation = getDeviation(monthSum, comparisonValue)

      comparisonRows.push(
        createComparisonTable(
          `${fmtCol(monthSum)} ${sensorUnit(medium)}`,
          `${fmtCol(deviation)} %`,
          `${fmtCol(comparisonValue)} ${sensorUnit(medium)}`
        )
      )
    }

    setTableDataComparison(comparisonRows)

    const co2Global = []
    const priceGlobal = []
    const priceBySqmGlobal = []
    const priceByEmployeeGlobal = []
    const consumptionEmployeeGlobal = []
    const consumptionSqmGlobal = []

    for (const key in result) {
      if (key === 'Gesamtverbrauch') continue

      const sheet = result[key]
      const sensor = sensorByNameMap.get(key)

      if (!sensor) continue

      for (const [index, record] of sheet.entries()) {
        const date = record[0]
        const consumptionMap = consumptionMapsByName.get(key)
        const timepoint = formatDate(date, format)
        const data = consumptionMap.get(timepoint)

        if (!data) continue

        const co2 = data.co2
        co2Global[date] = (co2Global[date] || 0) + co2

        const amount = data.cost
        priceGlobal[index] = (priceGlobal[index] || 0) + amount

        const amountBySqm = data.cost_per_sqm
        priceBySqmGlobal[date] = (priceBySqmGlobal[date] || 0) + amountBySqm

        const amountByEmployee = data.cost_per_employee
        priceByEmployeeGlobal[date] =
          (priceByEmployeeGlobal[date] || 0) + amountByEmployee

        const consumptionByEmployee = data.value_per_employee
        consumptionEmployeeGlobal[date] =
          (consumptionEmployeeGlobal[date] || 0) + consumptionByEmployee

        const consumptionBySqm = data.value_per_sqm
        consumptionSqmGlobal[date] =
          (consumptionSqmGlobal[date] || 0) + consumptionBySqm
      }
    }

    let meterUnit = ''

    for (const key in result) {
      const sheet = result[key]

      const sumPercent = 0
      let sumConsumption = 0
      let sumConsumptionBySqm = 0
      let sumConsumptionByEmployee = 0
      let sumCost = 0
      let sumCostBySqm = 0
      let sumCo2Consumption = 0
      let sumCostByEmployee = 0
      let consumptionGlobal = 1

      let totalCost = 0
      let consumptionPerSqm = 0
      let consumptionPerEmployee = 0
      let costPerSqm = 0
      let costPerEmployee = 0
      let co2Value = 0
      let costValue = 0
      let kpiBenchmark = 0

      const sensor = sensorByNameMap.get(key)

      for (let i = 0; i < sheet.length; i++) {
        const record = sheet[i]
        const date = record[0]
        let consumption = record[1]

        if (sensor) {
          const { benchmarks } = sensor
          const consumptionMap = consumptionMapsByName.get(key)
          const timepoint = formatDate(record[0], format)
          const data = consumptionMap.get(timepoint)

          if (data) {
            const {
              co2,
              value_per_sqm,
              value_per_employee,
              cost,
              cost_per_sqm,
              cost_per_employee,
            } = data
            co2Value = co2 ?? '-'
            costValue = cost ?? '-'
            consumptionPerSqm = value_per_sqm ?? '-'
            consumptionPerEmployee = value_per_employee ?? '-'
            costPerSqm = cost_per_sqm ?? '-'
            costPerEmployee = cost_per_employee ?? '-'

            const benchmarksOnDate = benchmarks.filter(benchmark =>
              isBenchmarkOnDate(benchmark, date)
            )

            if (benchmarksOnDate.length > 0)
              kpiBenchmark = benchmarksOnDate[0].attributes.value
          } else {
            co2Value = 0
            costValue = 0
            consumptionPerSqm = 0
            consumptionPerEmployee = 0
            costPerSqm = 0
            costPerEmployee = 0
          }
        }

        const percent =
          consumptionGlobal === 1
            ? 0
            : ((consumption - consumptionGlobal) / consumptionGlobal) * 100

        const trend =
          consumptionGlobal === 1
            ? 0
            : consumption > consumptionGlobal
              ? percent
              : percent

        consumptionGlobal = consumption

        consumption = checkAndReplaceNull(consumption)
        const costBySqm = getValueOrDefault(key, costPerSqm)
        const costByEmployee = getValueOrDefault(key, costPerEmployee)
        const consumptionBySqm = getValueOrDefault(key, consumptionPerSqm)
        const consumptionByEmployee = getValueOrDefault(
          key,
          consumptionPerEmployee
        )

        const consumptionCo2 =
          key === 'Gesamtverbrauch'
            ? co2Global[date] === undefined
              ? '-'
              : co2Global[date]
            : checkAndReplaceNull(co2Value)

        totalCost =
          key === 'Gesamtverbrauch'
            ? priceGlobal[i] === undefined
              ? '-'
              : priceGlobal[i]
            : checkAndReplaceNull(costValue)

        const totalConsumptionByEmployee =
          key === 'Gesamtverbrauch'
            ? consumptionEmployeeGlobal[date] === undefined
              ? '-'
              : consumptionEmployeeGlobal[date]
            : checkAndReplaceNull(consumptionByEmployee)

        const totalCostByEmployee =
          key === 'Gesamtverbrauch'
            ? priceByEmployeeGlobal[date] === undefined
              ? '-'
              : priceByEmployeeGlobal[date]
            : checkAndReplaceNull(costByEmployee)

        const totalConsumptionBySqm =
          key === 'Gesamtverbrauch'
            ? consumptionSqmGlobal[date] === undefined
              ? '-'
              : consumptionSqmGlobal[date]
            : checkAndReplaceNull(consumptionBySqm)

        const totalCostBySqm =
          key === 'Gesamtverbrauch'
            ? priceBySqmGlobal[date] === undefined
              ? '-'
              : priceBySqmGlobal[date]
            : checkAndReplaceNull(costBySqm)

        let meterReading = '-'

        const sensorConsumptions = operatorConsumptionByName.get(key)

        if (sensorConsumptions !== undefined && sensorConsumptions !== null) {
          const { meter_reading } = sensorConsumptions

          if (meter_reading !== undefined && meter_reading !== null) {
            const recordMeterReadingData = meterReadingByName.get(key)

            if (recordMeterReadingData) {
              const { meter_reading_date, meter_readings, meter_unit } =
                recordMeterReadingData

              const toDateKey = value => {
                if (!value) return ''

                // YYYY-MM-DD
                if (typeof value === 'string') return value.substring(0, 10)

                // Date-Object UTC
                const d = value

                return [
                  d.getFullYear(),
                  String(d.getMonth() + 1).padStart(2, '0'),
                  String(d.getDate()).padStart(2, '0'),
                ].join('-')
              }

              meterUnit = meter_unit

              if (granularityQuery === 'y') {
                let withoutWeekDay = new Date(record[0])
                const compareDate = new Date(meter_reading_date)

                if (
                  withoutWeekDay.getUTCMonth() === compareDate.getUTCMonth() &&
                  withoutWeekDay.getUTCFullYear() ===
                    compareDate.getUTCFullYear()
                )
                  withoutWeekDay = new Date(compareDate)

                if (withoutWeekDay >= compareDate) {
                  const lastDayDate = new Date(
                    Date.UTC(
                      withoutWeekDay.getUTCFullYear(),
                      withoutWeekDay.getUTCMonth() + 1,
                      0
                    )
                  )

                  const foundRecord = meter_readings.values.find(
                    f => toDateKey(f.timepoint) === toDateKey(lastDayDate)
                  )

                  if (foundRecord) meterReading = foundRecord.value
                }
              }

              if (granularityQuery === 'm') {
                const targetDate = toDateKey(record[0])

                const foundRecord = meter_readings.values.find(f => {
                  const readingDate = toDateKey(f.timepoint)

                  return readingDate === targetDate
                })

                if (foundRecord) meterReading = foundRecord.value
              }
            }
          }
        }

        const benchmark =
          granularityQuery === 'y' && kpiBenchmark !== 0
            ? ((costBySqm - kpiBenchmark) / kpiBenchmark) * 100
            : 0

        sumConsumption = addValueIfValid(consumption, sumConsumption)
        sumCo2Consumption = addValueIfValid(consumptionCo2, sumCo2Consumption)
        sumConsumptionBySqm = addValueIfValid(
          totalConsumptionBySqm,
          sumConsumptionBySqm
        )
        sumConsumptionByEmployee = addValueIfValid(
          totalConsumptionByEmployee,
          sumConsumptionByEmployee
        )
        sumCost = addValueIfValid(totalCost, sumCost)
        sumCostBySqm = addValueIfValid(totalCostBySqm, sumCostBySqm)
        sumCostByEmployee = addValueIfValid(
          totalCostByEmployee,
          sumCostByEmployee
        )

        sheet[i] = [
          date,
          trend,
          consumption,
          consumptionCo2,
          totalConsumptionBySqm,
          totalConsumptionByEmployee,
          totalCost,
          totalCostBySqm,
          benchmark,
          meterReading,
          totalCostByEmployee,
        ]
      }

      if (totalCost === 0 || isNaN(totalCost)) {
        if (isNaN(sumCost)) sumCost = 0
        if (isNaN(sumCostBySqm)) sumCostBySqm = 0
        if (isNaN(sumCostByEmployee)) sumCostByEmployee = 0
      }

      sumConsumption = replaceZeroOrNaN(sumConsumption)
      sumCo2Consumption = replaceZeroOrNaN(sumCo2Consumption)
      sumConsumptionBySqm = replaceZeroOrNaN(sumConsumptionBySqm)
      sumConsumptionByEmployee = replaceZeroOrNaN(sumConsumptionByEmployee)
      sumCost = replaceZeroOrNaN(sumCost)
      sumCostBySqm = replaceZeroOrNaN(sumCostBySqm)
      sumCostByEmployee = replaceZeroOrNaN(sumCostByEmployee)

      sheet.push([
        'Summe',
        sumPercent,
        sumConsumption,
        sumCo2Consumption,
        sumConsumptionBySqm,
        sumConsumptionByEmployee,
        sumCost,
        sumCostBySqm,
        '-',
        '-',
        sumCostByEmployee,
      ])
    }

    for (const key in result) {
      const sheet = result[key]
      const unit = sensorUnit(medium)

      for (let i = 0; i < sheet.length; i++) {
        const record = sheet[i]
        const date = record[0]
        const trend = fmtCol(record[1])

        const percent =
          record[1] > 0
            ? `▲ ${trend}`
            : record[1] < 0
              ? `▼ ${trend}`
              : record[1] === 0 || isNaN(record[1])
                ? '-'
                : record[1]

        const consumption =
          record[2] === null || record[2] === '-' || record[2] === 0
            ? '-'
            : record[2]

        const consumptionCo2 =
          record[3] === undefined || record[3] === '-' || isNaN(record[3])
            ? record[3]
            : `${fmtCol(record[3])} kg`

        const consumptionBySqm =
          record[4] === '-' ? record[4] : `${fmtCol(record[4])} ${unit}`

        const consumptionByEmployee =
          record[5] === '-' ? record[5] : `${fmtCol(record[5])} ${unit}`

        const totalCost =
          record[6] === '-' || record[6] === undefined || isNaN(record[6])
            ? record[6]
            : `${fmtCol(record[6])} €`

        const costBySqm =
          record[7] === '-' || record[7] === undefined || isNaN(record[7])
            ? record[7]
            : `${fmtCol(record[7])} €`

        const bTrend = record[8] === '-' ? record[8] : fmtCol(record[8])
        const benchmark =
          record[8] > 0
            ? `▲ ${bTrend}`
            : record[8] < 0
              ? `▼ ${bTrend}`
              : record[8] === 0 || isNaN(record[8])
                ? '-'
                : record[8]

        const counterReading =
          record[9] === '-' ? record[9] : `${fmtCol(record[9])} ${meterUnit}`

        const costByEmployee =
          record[10] === '-' || record[10] === undefined || isNaN(record[10])
            ? record[10]
            : `${fmtCol(record[10])} €`

        sheet[i] = [
          date,
          consumption,
          percent,
          consumptionCo2,
          consumptionBySqm,
          consumptionByEmployee,
          totalCost,
          costBySqm,
          benchmark,
          counterReading,
          costByEmployee,
        ]
      }
    }

    setTableDataExport(result)
  }, [
    areaInSqm,
    chartData,
    compareChartData,
    granularityQuery,
    fData,
    medium,
    sensorByNameMap,
    formatDate,
    isBenchmarkOnDate,
    consumptionMapsByName,
    operatorConsumptionByName,
    meterReadingByName,
  ])

  return (
    <KPage
      action={
        <IconButton onClick={openAM}>
          <MoreVertTwoTone />
        </IconButton>
      }
      avatar={
        <Avatar onClick={handleSettings}>
          <SVGStatus type={'performance'} status={fStatus.performance} />
        </Avatar>
      }
      title={title}
    >
      <Preloader
        caption={`Detailansicht Verbrauch laden ... ${percentage}%`}
        isLoading={isLoading}
        percentage={percentage}
      />

      <AM
        anchorEl={actionMenu}
        caption={title}
        historyUrlId={fId}
        historyUrlTarget={'performance'}
        onClose={closeAM}
        open={Boolean(actionMenu)}
      >
        {[
          {
            caption: 'Diagnose',
            disabled: !opData?.isAdmin,
            fn: openDiagnostics,
            icon: <CheckTwoTone />,
            id: 0,
          },
          {
            caption: 'Details zum Gebäude',
            fn: gotoFolderDetails,
            icon: <AccountBalanceTwoTone />,
            id: 1,
          },
          {
            caption: null,
            fn: null,
            icon: null,
            id: 2,
          },
          {
            caption: 'Leistung',
            fn: gotoPerformanceOverview,
            icon: (
              <SVGStatus type={'performance'} status={fStatus.performance} />
            ),
            id: 3,
          },
          {
            caption: 'Anlagen',
            fn: gotoInstallationOverview,
            icon: (
              <SVGStatus type={'installation'} status={fStatus.installation} />
            ),
            id: 4,
          },
          {
            caption: 'Korrelation',
            fn: gotoCorrelation,
            icon: (
              <SVGStatus type={'correlation'} status={fStatus.correlation} />
            ),
            id: 5,
          },
          {
            caption: 'Sicherheit',
            fn: gotoSecurity,
            icon: <SVGStatus type={'security'} status={fStatus.security} />,
            id: 6,
          },
          {
            caption: 'Wohlbefinden',
            fn: gotoFeeling,
            icon: <SVGStatus type={'feeling'} status={fStatus.feeling} />,
            id: 7,
          },
        ].map(item =>
          item?.caption !== null ? (
            <AMItem
              caption={item?.caption}
              disabled={item?.disabled || false}
              icon={
                <Stack alignItems={'center'} sx={{ width: 25 }}>
                  {item?.icon}
                </Stack>
              }
              key={item?.id}
              onClick={item?.fn}
            />
          ) : (
            <Divider key={item.id} />
          )
        )}
      </AM>
      {/* action menu end */}

      {/* diagnose */}
      {!isLoading && opData?.isAdmin && (
        <DlgMediumDiagnostics
          allSensors={allSensors}
          medium={medium}
          fillUpSensors={fillUpSensors}
          id={fId}
          onClose={closeDiagnostics}
          open={query.diagnosis || false}
          title={periodData.title}
        />
      )}
      {/* diagnose end */}

      {/* tabellen spalten konfiguration */}
      {!isLoading && (
        <DlgConsumptionTableCols
          onClose={closeTableRowsDialog}
          onConfirm={configureTableColumns}
          open={query.tableRows || false}
          id={fId}
        />
      )}
      {/* tabellen spalten konfiguration end */}

      {/* exportieren */}
      {exportModal && (
        <DlgConsumptionExport
          open={exportModal}
          onClose={closeExportDialog}
          exportAsJPEG={exportAsJPEG}
          exportAsPNG={exportAsPNG}
          id={fId}
          options={{
            data: tableDataExport,
            fileName,
            granularity: granularityQuery,
            medium,
            timespan: periodData?.title,
            kpiLabel: kpiData?.label,
            viewCols,
            unitConfig,
          }}
        />
      )}
      {/* exportieren end */}

      <Card variant={'performance'}>
        <CardContent sx={{ p: '0px !important' }}>
          {sPool.length > 0 ? (
            <Box
              overflow={'auto'}
              sx={{ borderBottom: 'solid 1px #B9B9B9', px: 1 }}
            >
              <HStack justifyContent={'space-between'}>
                <HStack alignItems={'center'} spacing={1}>
                  <MediumSelector
                    consumptionData={consumptionData}
                    id={fId}
                    onChange={handleMedium}
                    sx={{ width: 150 }}
                    value={medium}
                  />

                  <KPISelector
                    onChange={handleKpiType}
                    sx={{ width: 120 }}
                    value={kpiData.type}
                  />

                  <StackSelector
                    disabled={isStackDisabled}
                    onChange={handleStackMode}
                    value={query.stacked}
                  />

                  <ChartTypeSelector
                    onChange={handleChartType}
                    value={query.chartType}
                  />

                  <Tooltip title={'Exportieren'}>
                    <span>
                      <IconButton
                        disabled={selectedSensors.length === 0}
                        onClick={openExportDialog}
                      >
                        <DownloadTwoTone />
                      </IconButton>
                    </span>
                  </Tooltip>
                </HStack>

                <HStack alignItems={'center'} spacing={1}>
                  <IconButton
                    disabled={isBrowseBackDisabled}
                    onClick={browseBack}
                  >
                    <ArrowBackIosTwoTone />
                  </IconButton>

                  <IconButton
                    disabled={isBrowseForwardDisabled}
                    onClick={browseForward}
                  >
                    <ArrowForwardIosTwoTone />
                  </IconButton>

                  <GranularitySelector
                    onChange={handleGranularity}
                    value={newParams?.granularity}
                  />

                  <GranularityDateSelector
                    granularity={newParams?.granularity}
                    id={fId}
                    label={'von'}
                    mode={'from'}
                    onChange={handleFromDate}
                    value={newParams?.fromDate}
                  />

                  <GranularityDateSelector
                    granularity={newParams?.granularity}
                    id={fId}
                    label={'bis'}
                    mode={'to'}
                    onChange={handleToDate}
                    value={newParams?.toDate}
                  />

                  <DayFilterSelector
                    disabled={isDaysFilterDisabled}
                    resetString={isDaysFilterDisabled ? true : false}
                    onChange={handleDaysFilter}
                    selectedDays={selectedDays}
                    defaultSelectedDays={defaultSelectedDays}
                  />

                  <Button
                    color={'primary'}
                    disabled={configChanged}
                    onClick={applyConfigChange}
                  >
                    Anwenden
                  </Button>
                </HStack>
              </HStack>
            </Box>
          ) : (
            <Skeleton variant={'rectangular'} width={'100%'} height={60} />
          )}

          <Collapse in={periodData.showWarning}>
            <Alert severity={'warning'}>{periodData.warningText}</Alert>
          </Collapse>

          <Collapse in={periodData.showDataWarning}>
            <Alert severity={'warning'}>{periodData.warningDataText}</Alert>
          </Collapse>

          <Collapse in={isFactorError || isFactorErrorMeterReading}>
            <Alert severity={'warning'}>
              {`Bitte prüfen Sie die Faktoren für Messpunkt ${
                factorError ? factorError : factorErrorSensorMeterReading
              }.`}
            </Alert>
          </Collapse>

          <HStack
            className={'image-export'}
            sx={{
              display: 'flex',
              bgcolor,
              flexDirection:
                exportModal && sensorsUnderChart ? 'column' : 'row',
            }}
          >
            {sPool.length > 0 ? (
              <Stack sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <Stack
                  alignItems={'center'}
                  justifyContent={'flex-start'}
                  sx={{ bgcolor }}
                >
                  <Typography sx={{ fontSize: 20, fontWeight: 'bold', my: 2 }}>
                    {periodData.title}
                  </Typography>

                  <KPIViewer
                    medium={medium}
                    kpiData={kpiData}
                    sx={{ pt: 2 }}
                    exportModal={exportModal}
                  />
                </Stack>

                {consumptionReady && MemoChart}

                <Collapse
                  in={selectedSensors.length === 0}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <Alert severity={'warning'}>
                    Bitte wählen Sie einen oder mehrere Sensoren aus.
                  </Alert>
                </Collapse>
              </Stack>
            ) : (
              <Skeleton variant={'rectangular'} width={'100%'} height={650} />
            )}

            <Stack
              sx={{
                flex: '0 0 auto',
                minWidth: exportModal && sensorsUnderChart ? '100%' : 300,
                maxWidth: exportModal && sensorsUnderChart ? '100%' : '20vw',
              }}
            >
              <SensorSelector
                id={fId}
                onChange={handleSelectedSensors}
                onChangeOrder={handleOrder}
                onChangeInfo={setShowInfo}
                options={{
                  data: allSensors,
                  exportModal,
                  fromDate: fromDateQuery,
                  medium,
                  order,
                  selected: selectedSensors,
                  sensorsUnderChart,
                }}
              />
            </Stack>
          </HStack>
        </CardContent>
      </Card>

      <Collapse in={!periodData.showWarning}>
        <ConsumptionTable
          id={fId}
          key={tableKey} // dont remove
          colors={sensorColors}
          cols={viewCols}
          data={tableDataExport}
          granularity={granularityTitle(granularityQuery)}
          medium={medium}
          onClick={openTableRowsDialog}
          unitConfig={unitConfig}
        />
      </Collapse>
    </KPage>
  )
}
